// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useLocalSearchParams } from 'expo-router';
import { StyleSheet, Text, View } from 'react-native';

import { useUser } from '@/app/user-context';
import { StakeCaptureScreen } from '@/features/course-stakes/StakeCaptureScreen';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

/**
 * Recording boundary stakes on a course, opened from the golf-course admin page. Admin only:
 * a stake becomes the out-of-bounds line every player of the course is shown.
 */
export default function CourseStakesScreen() {
  const { t } = useI18n();
  const { courseId, name, holes } = useLocalSearchParams<{ courseId?: string; name?: string; holes?: string }>();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const id = typeof courseId === 'string' && courseId.length > 0 ? courseId : null;
  const holesCount = typeof holes === 'string' && Number.isFinite(Number(holes)) ? Number(holes) : 18;

  return (
    <>
      <Stack.Screen options={{ title: t('courseStakes.title') }} />
      <View style={styles.container}>
        <Text style={styles.title}>
          {typeof name === 'string' && name.length > 0 ? name : t('courseStakes.title')}
        </Text>
        {!isAdmin ? (
          <Text style={styles.paragraph} testID="course-stakes-forbidden">
            {t('courseStakes.adminOnly')}
          </Text>
        ) : id === null ? (
          <Text style={styles.paragraph} testID="course-stakes-missing-course">
            {t('courseStakes.noCourse')}
          </Text>
        ) : (
          <StakeCaptureScreen courseId={id} holesCount={holesCount} token={user?.token} />
        )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, paddingTop: 16, gap: 8 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary, paddingHorizontal: 16 },
  paragraph: { fontSize: 15, color: appColors.text.secondary, paddingHorizontal: 16 },
});
